/**
 * ============================================================
 * GREENWAY BIOTECH - UNIVERSAL CALCULATOR DISPATCHER
 * ============================================================
 * Version: 5.3.1
 * Updated: October 2026
 *   5.3.1: parseVariants also reads the theme's <script class="product-json"> for this
 *          page's product; recommendPackage picks the single size with the lowest total
 *          price; parseWeight reads the net weight outside a parenthetical yield or pack
 *          note ("2.5 Pounds (Makes 1 Gallon)", "50 Pounds (or 2 x 25 pound bags)").
 *
 * FEATURES:
 *   - Dynamic script loading on-demand (no theme.liquid edits needed)
 *   - Uses explicit theme asset URLs or its own script location
 *   - Variant parsing utilities for package recommendations
 *
 * HOW TO ADD A NEW CALCULATOR:
 *   1. Upload calculator JS file to Shopify Assets
 *   2. Add entry to CALCULATORS registry below
 *   3. Done! No theme.liquid changes required
 *
 * USAGE IN PRODUCT HTML:
 *   <div class="gwb-calculator" data-calc="product-key"></div>
 */

(function() {
    'use strict';

    // ============================================================
    // CALCULATOR REGISTRY
    // ============================================================

    const CALCULATORS = {
        // ─── WELLNESS / PERSONAL CARE ───
        'mg-oil': {
            name: 'Magnesium Oil Calculator',
            initFnName: 'renderMgOilCalculator',
            file: 'mg-oil-calculator.js',
            available: true
        },
        'skin-quiz': {
            name: 'Skin Care Finder Quiz',
            initFnName: 'renderSkinQuizCalculator',
            file: 'skin-quiz-calculator.js',
            available: true
        },
        'msm-powder': {
            name: 'MSM Supplement Calculator',
            initFnName: 'renderMsmPowderCalculator',
            file: 'msm-powder-calculator.js',
            available: true
        },
        'mg-chloride': {
            name: 'Magnesium Chloride Calculator',
            initFnName: 'renderMgChlorideCalculator',
            file: 'mg-chloride-calculator.js',
            available: true
        },
        'epsom-salt': {
            name: 'Epsom Salt Calculator',
            initFnName: 'renderEpsomSaltCalculator',
            file: 'epsom-salt-calculator.js',
            available: true
        },
        'health-bundle': {
            name: 'Health Bundle Calculator',
            initFnName: 'renderHealthBundleCalculator',
            file: 'health-bundle-calculator.js',
            available: true
        },

        // ─── HYDROPONIC FERTILIZERS ───
        'tomato fertilizer calculator v1': {
            name: 'Tomato Fertilizer Calculator',
            initFnName: 'renderTomatoFertilizerCalculatorV1',
            file: 'tomato-fertilizer-calculator-v1.js',
            available: true
        },
        'lettuce fertilizer calculator v1': {
            name: 'Lettuce Fertilizer Calculator V1',
            initFnName: 'renderLettuceFertilizerCalculatorV1',
            file: 'lettuce-fertilizer-calculator-v1.js',
            available: true
        },
        'cucumber fertilizer calculator v1': {
            name: 'Cucumber Fertilizer Calculator V1',
            initFnName: 'renderCucumberFertilizerCalculatorV1',
            file: 'cucumber-fertilizer-calculator-v1.js',
            available: true
        },
        'map 11-52-0 fertilizer calculator v1': {
            name: 'MAP 11-52-0 Fertilizer Calculator V1',
            initFnName: 'renderMap11520FertilizerCalculatorV1',
            file: 'map-11-52-0-fertilizer-calculator-v1.js',
            available: true
        },
        'map 12-61-0 fertilizer calculator v1': {
            name: 'MAP 12-61-0 Fertilizer Calculator V1',
            initFnName: 'renderMAP12610FertilizerCalculatorV1',
            file: 'MAP-12-61-0-fertilizer-calculator-v1.js',
            available: true
        },
        'azomite v2': {
            name: 'Azomite Calculator V2',
            initFnName: 'renderAzomiteCalculatorV2',
            file: 'azomite-calculator-v2.js',
            available: true
        },
        'pepper herb fertilizer calculator v1': {
            name: 'Pepper Herb Fertilizer Calculator',
            initFnName: 'renderPepperHerbFertilizerCalculatorV1',
            file: 'pepper-herb-fertilizer-calculator-v1.js',
            available: true
        },
        'strawberry fertilizer calculator v1': {
            name: 'Strawberry Fertilizer Calculator',
            initFnName: 'renderStrawberryFertilizerCalculatorV1',
            file: 'strawberry-fertilizer-calculator-v1.js',
            available: true
        },
        'rose flower fertilizer calculator v1': {
            name: 'Rose & Flower Fertilizer Calculator V1',
            initFnName: 'renderRoseFlowerFertilizerCalculatorV1',
            file: 'rose-flower-fertilizer-calculator-v1.js',
            available: true
        },
        'citrus avocado fertilizer calculator v3': {
            name: 'Citrus & Avocado Fertilizer Calculator V3',
            initFnName: 'renderCitrusAvocadoFertilizerCalculatorV3',
            file: 'citrus-avocado-fertilizer-calculator-v3.js',
            available: true
        },
        'azalea camellia fertilizer calculator v2': {
            name: 'Azalea & Camellia Fertilizer Calculator V2',
            initFnName: 'renderAzaleaCamelliaFertilizerCalculatorV2',
            file: 'azalea-camellia-fertilizer-calculator-v2.js',
            available: true
        },
        'hydroponics': {
            name: 'Hydroponics Calculator',
            initFnName: 'renderHydroponicsCalculator',
            file: 'hydroponics-calculator.js',
            available: true
        },
        'monopotassium phosphate calculator v1': {
            name: 'Monopotassium Phosphate Calculator V1',
            initFnName: 'renderMonopotassiumPhosphateCalculatorV1',
            file: 'monopotassium-phosphate-calculator-v1.js',
            available: true
        },
        'blossom green calculator v3': {
            name: 'Blossom Green Calculator V3',
            initFnName: 'renderBlossomGreenCalculatorV3',
            file: 'blossom-green-calculator-v3.js',
            available: true
        },
        'micro green calculator v2': {
            name: 'Micro Green Calculator V2',
            initFnName: 'renderMicroGreenCalculatorV2',
            file: 'micro-green-calculator-v2.js',
            available: true
        },
        'grow green v2': {
            name: 'Grow Green Calculator V2',
            initFnName: 'renderGrowGreenCalculatorV2',
            file: 'grow-green-calculator-v2.js',
            available: true
        },
        'cal mag calculator v1': {
            name: 'Cal Mag Calculator V1',
            initFnName: 'renderCalMagCalculatorV1',
            file: 'cal-mag-calculator-v1.js',
            available: true
        },
        'hydroponic-bundle': {
            name: '4-Part Hydroponic Bundle Calculator',
            initFnName: 'renderHydroponicBundleCalculator',
            file: 'hydroponic-bundle-calculator.js',
            available: true
        },
        'houseplant food calculator v1': {
            name: 'Houseplant Food Calculator',
            file: 'houseplant-food-calculator-v1.js',
            initFnName: 'renderHouseplantFoodCalculatorV1',
            available: true
        },

        // ─── CHELATED MICRONUTRIENTS ───
        'chelated copper edta calculator v2': {
            name: 'Chelated Copper Calculator V2',
            initFnName: 'renderChelatedCopperEDTACalculatorV2',
            file: 'chelated-copper-edta-calculator-v2.js',
            available: true
        },
        'chelated iron dtpa calculator v2': {
            name: 'Chelated Iron DTPA Calculator V2',
            initFnName: 'renderChelatedIronDTPACalculatorV2',
            file: 'chelated-iron-dtpa-calculator-v2.js',
            available: true
        },
        'chelated iron edta calculator v1': {
            name: 'Chelated Iron EDTA Calculator V1',
            initFnName: 'renderChelatedIronEDTACalculatorV1',
            file: 'chelated-iron-edta-calculator-v1.js',
            available: true
        },
        'chelated manganese edta calculator v1': {
            name: 'Chelated Manganese EDTA Calculator V1',
            initFnName: 'renderChelatedManganeseEDTACalculatorV1',
            file: 'chelated-manganese-edta-calculator-v1.js',
            available: true
        },
        'chelated zinc edta calculator v1': {
            name: 'Chelated Zinc EDTA Calculator V1',
            initFnName: 'renderChelatedZincEDTACalculatorV1',
            file: 'chelated-zinc-edta-calculator-v1.js',
            available: true
        },

        // ─── SOIL AMENDMENTS / NITROGEN SOURCES ───
        'feather meal calculator v1': {
            name: 'Feather Meal Calculator V1',
            initFnName: 'renderFeatherMealCalculatorV1',
            file: 'feather-meal-calculator-v1.js',
            available: true
        },
        'cottonseed meal v2': {
            name: 'cottonseed Meal Calculator V2',
            initFnName: 'renderCottonseedMealCalculatorV2',
            file: 'cottonseed-meal-calculator-v2.js',
            available: true
        },
        'kelp meal calculator v2': {
            name: 'Kelp Meal Calculator V2',
            initFnName: 'renderKelpMealCalculatorV2',
            file: 'kelp-meal-calculator-v2.js',
            available: true
        },
        'alfalfa meal calculator v1': {
            name: 'Alfalfa Meal Calculator V1',
            initFnName: 'renderAlfalfaMealCalculatorV1',
            file: 'alfalfa-meal-calculator-v1.js',
            available: true
        },
        'bone meal v1': {
            name: 'Bone Meal Calculator V1',
            initFnName: 'renderBoneMealCalculatorV1',
            file: 'bone-meal-calculator-v1.js',
            available: true
        },
        'blood meal v2': {
            name: 'Blood Meal Calculator V2',
            initFnName: 'renderBloodMealCalculatorV2',
            file: 'blood-meal-calculator-v2.js',
            available: true
        },
        'fish bone meal calculator v1': {
            name: 'Fish Bone Meal Calculator V1',
            initFnName: 'renderFishBoneMealCalculatorV1',
            file: 'fish-bone-meal-calculator-v1.js',
            available: true
        },
        'calcium nitrate calculator v2': {
            name: 'Calcium Nitrate Calculator V2',
            initFnName: 'renderCalciumNitrateCalculatorV2',
            file: 'calcium-nitrate-calculator-v2.js',
            available: true
        },
        'magnesium nitrate calculator v1': {
            name: 'Magnesium Nitrate Calculator V1',
            initFnName: 'renderMagnesiumNitrateCalculatorV1',
            file: 'magnesium-nitrate-calculator-v1.js',
            available: true
        },
        'urea calculator v5': {
            name: 'Urea Calculator v5',
            initFnName: 'renderUreaCalculatorV5',
            file: 'urea-calculator-v5.js',
            available: true
        },
        'nitroform calculator v1': {
            name: 'Nitroform Calculator V1',
            initFnName: 'renderNitroformCalculatorV1',
            file: 'nitroform-calculator-v1.js',
            available: true
        },
        'gypsum calculator v1': {
            name: 'Gypsum Calculator V1',
            initFnName: 'renderGypsumCalculatorV1',
            file: 'gypsum-calculator-v1.js',
            available: true
        },
        'dolomite calculator v2': {
            name: 'Dolomite Calculator V2',
            initFnName: 'renderDolomiteCalculatorV2',
            file: 'dolomite-calculator-v2.js',
            available: true
        },
        'sulfur calculator v1': {
            name: 'Sulfur Calculator V1',
            initFnName: 'renderSulfurCalculatorV1',
            file: 'sulfur-calculator-v1.js',
            available: true
        },
        'boric acid v3': {
            name: 'Boric Acid Calculator V3',
            initFnName: 'renderBoricAcidCalculatorV3',
            file: 'boric-acid-calculator-v3.js',
            available: true
        },
        'k-mag calculator v2': {
            name: 'K-Mag Calculator V2',
            initFnName: 'renderKMagCalculatorV2',
            file: 'kmag-calculator-v2.js',
            available: true
        },
        'sodium molybdate v2': {
            name: 'Sodium Molybdate Calculator V2',
            initFnName: 'renderSodiumMolybdateCalculatorV2',
            file: 'sodium-molybdate-calculator-v2.js',
            available: true
        },

        'ammonium sulfate calculator v2': {
            name: 'Ammonium Sulfate Calculator V2',
            initFnName: 'renderAmmoniumSulfateCalculatorV2',
            file: 'ammonium-sulfate-calculator-v2.js',
            available: true
        },
        'zinc sulfate calculator v1': {
            name: 'Zinc Sulfate Calculator V1',
            initFnName: 'renderZincSulfateCalculatorV1',
            file: 'zinc-sulfate-calculator-v1.js',
            available: true
        },
        'ferrous sulfate calculator V2': {
            name: 'Ferrous Sulfate Calculator V2',
            initFnName: 'renderFerrousSulfateCalculatorV2',
            file: 'ferrous-sulfate-calculator-v2.js',
            available: true
        },
        'potassium sulfate calculator v1': {
            name: 'Potassium Sulfate Calculator V1',
            initFnName: 'renderPotassiumSulfateCalculatorV1',
            file: 'potassium-sulfate-calculator-v1.js',
            available: true
        },
        'potassium chloride calculator v1': {
            name: 'Potassium Chloride Calculator',
            initFnName: 'renderPotassiumChlorideCalculatorV1',
            file: 'potassium-chloride-calculator-v1.js',
            available: true
        },
        'copper sulfate calculator v1': {
            name: 'Copper Sulfate Calculator V1',
            initFnName: 'renderCopperSulfateCalculatorV1',
            file: 'copper-sulfate-calculator-v1.js',
            available: true
        },
        'manganese sulfate v3': {
            name: 'Manganese Sulfate Calculator V3',
            initFnName: 'renderManganeseSulfateCalculatorV3',
            file: 'manganese-sulfate-calculator-v3.js',
            available: true
        },
        'crustacean meal calculator v1': {
            name: 'Crustacean Meal Calculator V1',
            initFnName: 'renderCrustaceanMealCalculatorV1',
            file: 'crustacean-meal-calculator-v1.js',
            available: true
        },
        'nitrogen-quiz': {
            name: 'Nitrogen Fertilizer Finder Quiz',
            initFnName: 'renderNitrogenQuizCalculator',
            file: 'nitrogen-quiz-calculator.js',
            available: true
        },
        'magnesium-quiz': {
            name: 'Personal Care Finder Quiz (Magnesium Finder page)',
            initFnName: 'renderMagnesiumQuizCalculator',
            file: 'magnesium-quiz-calculator-v2.js',
            available: true
        },
        'magnesium-combined': {
            name: 'Magnesium Combined Calculator',
            initFnName: 'renderMagnesiumCombinedCalculator',
            file: 'magnesium-combined-calculator.js',
            available: true
        },
        'hydro-quiz': {
           name: 'Hydroponics Nutrient Finder Quiz',
           initFnName: 'renderHydroQuizCalculator',
           file: 'hydro-quiz-calculator.js',
           available: true
        },
        'fertilizer-finder-quiz': {
           name: 'Fertilizer Finder Quiz',
           initFnName: 'renderFertilizerFinderQuizCalculator',
           file: 'fertilizer-finder-quiz-v2.js',
           available: true
        },
        'organic-fertilizer-quiz': {
           name: 'Organic Fertilizer Finder Quiz',
           initFnName: 'renderOrganicFertilizerQuizCalculator',
           file: 'organic-fertilizer-quiz-calculator.js',
           available: true
        },
        'phosphorus-quiz': {
           name: 'Phosphorus Fertilizer Finder Quiz',
           initFnName: 'renderPhosphorusQuizCalculator',
           file: 'phosphorus-quiz-v4.js',         
           available: true
        },
        'nutrient-diagnostic-quiz': {
           name: 'Nutrient Diagnostic Quiz (K vs Mn)',
           initFnName: 'renderNutrientDiagnosticQuizCalculator',
           file: 'nutrient-diagnostic-quiz-calculator-v2.js',
           available: true
        },
        'soil-ph-quiz': {
           name: 'Soil pH Amendment Finder Quiz',
           initFnName: 'renderSoilPhQuizCalculator',
           file: 'soil-ph-quiz-calculator.js',
           available: true
        },
        'calcium-quiz': {
           name: 'Calcium Source Finder Quiz',
           initFnName: 'renderCalciumQuizCalculator',
           file: 'calcium-quiz-calculator.js',
           available: true
        }


        // ─── ADD NEW CALCULATORS ABOVE THIS LINE ───
    };

    // Shared helpers v5.3.1. Existing calculator keys and function signatures retained.
    const GWB = window.GWB = window.GWB || {};
    const ownScript = document.currentScript;
    const scriptLoads = new Map();
    const initJobs = new WeakMap();
    const productLoads = new Map();
    const massFactor = {lb:1,lbs:1,pound:1,pounds:1,oz:1/16,ounce:1/16,ounces:1/16,kg:1/0.45359237,kilogram:1/0.45359237,kilograms:1/0.45359237,g:1/453.59237,gram:1/453.59237,grams:1/453.59237};
    // Net weight first: a title such as "2.5 Pounds (Makes 1 Gallon)" or "50 Pounds (or 2 x 25 pound bags)" carries a
    // yield or pack note in parentheses. Read the weight outside the note; if that finds none, parse the whole title as before.
    function parseWeight(title) {
        if (typeof title !== 'string') return null;
        const outside = title.replace(/\([^()]*\)/g, ' ').replace(/\s+/g, ' ').trim();
        if (outside && outside !== title.trim()) { const net = parseWeightText(outside); if (net !== null) return net; }
        return parseWeightText(title);
    }
    function parseWeightText(title) {
        if (typeof title !== 'string' || /\b(fl\.?\s*oz|fluid|gallons?|lit(?:er|re)s?|ml)\b/i.test(title)) return null;
        const number = '(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?';
        const re = new RegExp('(?:^|[^\\w.,])(?:(' + number + ')\\s*[x×]\\s*)?(' + number + ')\\s*(kilograms?|kg|pounds?|lbs?|ounces?|oz|grams?|g)\\b', 'gi');
        const matches = Array.from(title.matchAll(re));
        if (matches.length !== 1) return null; // Ambiguous titles require explicit net content.
        const m = matches[0];
        if (/^-\d/.test(m[0]) || /^\s*[x×]\s*\d/i.test(title.slice(m.index+m[0].length))) return null;
        const count = m[1] ? Number(m[1].replace(/,/g,'')) : 1;
        const amount = Number(m[2].replace(/,/g,''));
        const weight = count * amount * massFactor[m[3].toLowerCase()];
        if (/\d\s*-\s*$/.test(title.slice(0,m.index + m[0].indexOf(m[2])))) return null;
        if (/(?:pack\s+of|count|case\s+of)/i.test(title) && !m[1]) return null;
        return Number.isFinite(weight) && weight > 0 && Number.isInteger(count) ? weight : null;
    }
    function normalizeVariants(list) {
        if (!Array.isArray(list)) return [];
        return list.map(v => {
            const explicit = Number(v.net_weight_lb);
            const weight = Number.isFinite(explicit) && explicit > 0 ? explicit : parseWeight(v.public_title || v.title || v.name || '');
            const price = (typeof v.price === 'number' || (typeof v.price === 'string' && /^\d+(?:\.\d+)?$/.test(v.price))) ? Number(v.price) : NaN;
            return {id:v.id,title:String(v.public_title || v.title || v.name || ''),price,weight,available:v.available === true};
        }).filter(v => v.weight > 0 && Number.isFinite(v.weight) && /^\d+$/.test(String(v.id))).sort((a,b)=>a.weight-b.weight);
    }
    function parseVariants(container) {
        const raw = container && container.getAttribute('data-variants');
        if (raw) { try { const parsed = normalizeVariants(JSON.parse(raw)); if(parsed.length) return parsed; } catch (_) {} }
        const nodes = document.querySelectorAll('[data-product-json], #product-json, script[type="application/json"][data-product]');
        for(const node of nodes) { try {const data=JSON.parse(node.textContent);const parsed=normalizeVariants(data.variants || (data.product && data.product.variants));if(parsed.length)return parsed;}catch(_){} }
        // Theme fallback: snippets/product-json.liquid prints <script class="product-json" type="application/json">.
        // Other sections can print the same tag for other products, so accept only the one whose handle is this
        // page's product (data-product-handle, else /products/<handle> in the URL), or the only one on the page.
        const handle = pageProductHandle(container);
        const themeNodes = document.querySelectorAll('script.product-json[type="application/json"]');
        for(const node of themeNodes) { try {const data=JSON.parse(node.textContent);if(handle ? data.handle === handle : themeNodes.length === 1){const parsed=normalizeVariants(data.variants);if(parsed.length)return parsed;}}catch(_){} }
        // Analytics does not reliably carry sellability. Do not manufacture availability.
        return [];
    }
    function pageProductHandle(container) {
        const attr = container && container.getAttribute && container.getAttribute('data-product-handle');
        if (attr && /^[a-z0-9][a-z0-9-]*$/.test(attr)) return attr;
        const m = /\/products\/([a-z0-9][a-z0-9-]*)/i.exec((window.location && window.location.pathname) || '');
        return m ? m[1].toLowerCase() : '';
    }
    // Single-size picker: the caller buys ceil(need / weight) of the one variant returned (or 1 when it covers the need).
    // Choose the size whose total for that count is lowest, as recommendPackages does for mixed sizes; ties go to fewer
    // bags, then less left over. Without usable prices, fall back to the smallest size that covers the need, else the largest.
    function recommendPackage(variants, neededLbs) {
        if (!Number.isFinite(neededLbs) || neededLbs <= 0 || !Array.isArray(variants)) return null;
        const usable = variants.filter(v => v.available === true && Number.isFinite(v.weight) && v.weight > 0).slice().sort((a,b)=>a.weight-b.weight);
        let best = null, bestCost = Infinity, bestBags = Infinity, bestLeft = Infinity;
        for (const v of usable) {
            if (!Number.isFinite(v.price) || v.price < 0) continue;
            const bags = v.weight >= neededLbs ? 1 : Math.ceil(neededLbs / v.weight);
            const cost = bags * v.price, left = bags * v.weight - neededLbs;
            if (cost < bestCost || (cost === bestCost && (bags < bestBags || (bags === bestBags && left < bestLeft)))) { best = v; bestCost = cost; bestBags = bags; bestLeft = left; }
        }
        if (best) return best;
        return usable.find(v=>v.weight >= neededLbs) || usable[usable.length-1] || null;
    }
    function currency() {return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';}
    function formatPrice(minorUnits, code) {
        const value = Number(minorUnits);
        if (minorUnits === null || minorUnits === '' || !Number.isFinite(value) || value < 0) return '';
        try {return new Intl.NumberFormat(document.documentElement.lang || 'en-US',{style:'currency',currency:code || currency()}).format(value/100);} catch(_){return (value/100).toFixed(2)+' '+(code||currency());}
    }
    function escapeHtml(value) {return String(value == null ? '' : value).replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));}
    function routeRoot() {return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';}
    async function getProductData(handle, fresh) {
        if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) throw new Error('Invalid product handle');
        if (fresh) productLoads.delete(handle);
        if(!productLoads.has(handle)) {
            const job = fetch(routeRoot()+'products/'+encodeURIComponent(handle)+'.js',{credentials:'same-origin',cache:'no-store'})
                .then(r=>{if(!r.ok)throw new Error('Product options unavailable');return r.json();})
                .then(p=>({variants:normalizeVariants(p.variants),currency:currency(),product:p}))
                .catch(err=>{productLoads.delete(handle);throw err;});
            productLoads.set(handle,job);
        }
        return productLoads.get(handle);
    }
    // Exact unlimited-quantity optimization on a lossless weight grid. Final inventory is checked by cart API.
    function recommendPackages(variants, neededLbs) {
        const list=(variants||[]).filter(v=>v.available===true && Number.isFinite(v.weight) && v.weight>0 && Number.isSafeInteger(v.price) && v.price>=0);
        if(!Number.isFinite(neededLbs)||neededLbs<=0||!list.length)return null;
        const units=list.map(v=>Math.round(v.weight*1000));
        if(units.some((u,i)=>u<=0 || Math.abs(u/1000-list[i].weight)>1e-8))return null;
        const gcd=(a,b)=>b?gcd(b,a%b):a;
        const step=units.reduce(gcd);
        const sizes=units.map(u=>u/step),target=Math.ceil((neededLbs*1000-1e-8)/step);
        const limit=target+Math.max(...sizes)-1;
        if(!Number.isSafeInteger(limit)||limit>100000)return null;
        const costs=new Float64Array(limit+1);costs.fill(Infinity);costs[0]=0;
        const counts=new Int32Array(limit+1);const prev=new Int32Array(limit+1);prev.fill(-1);
        for(let w=1;w<=limit;w++)for(let i=0;i<list.length;i++) {
            const before=w-sizes[i]; if(before<0||!Number.isFinite(costs[before]))continue;
            const cost=costs[before]+list[i].price, bags=counts[before]+1;
            if(cost<costs[w] || (cost===costs[w] && bags<counts[w])){costs[w]=cost;counts[w]=bags;prev[w]=i;}
        }
        let cheapest=-1,least=-1;
        for(let w=target;w<=limit;w++)if(Number.isFinite(costs[w])) {
            if(least<0)least=w;
            if(cheapest<0||costs[w]<costs[cheapest])cheapest=w;
        }
        function plan(w) {
            if(w<0)return null;
            const q=new Map(),totalWeight=w*step/1000,totalPrice=costs[w];
            for(let n=w;n>0;){const i=prev[n];if(i<0)return null;q.set(i,(q.get(i)||0)+1);n-=sizes[i];}
            return {items:Array.from(q,([i,quantity])=>({variant:list[i],quantity})),totalWeight,totalPrice,leftover:Math.max(0,totalWeight-neededLbs)};
        }
        return {lowestPrice:plan(cheapest),leastLeftover:plan(least)};
    }
    async function addItems(items) {
        if(!Array.isArray(items)||!items.length || items.some(x=>!/^\d+$/.test(String(x.id))||!Number.isSafeInteger(x.quantity)||x.quantity<1))throw new Error('Invalid bag selection');
        const r=await fetch(routeRoot()+'cart/add.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const data=await r.json();if(!r.ok)throw new Error(data.description||data.message||'Unable to add these bags. Check availability and try again.');return data;
    }
function buildAssetUrl(filename) {
    if (!/^[a-z0-9._-]+\.js$/i.test(filename)) {
        throw new Error('Invalid asset filename');
    }

    if (
        GWB.assetUrls &&
        GWB.assetUrls[filename]
    ) {
        return GWB.assetUrls[filename];
    }

    const loaderScript =
        ownScript && ownScript.src
            ? ownScript
            : document.querySelector(
                'script[src*="gwb-calculators.js"]'
            );

    if (loaderScript && loaderScript.src) {
        const loaderUrl = new URL(loaderScript.src);
        const assetUrl = new URL(
            filename,
            loaderScript.src
        );

        const loaderVersion =
            loaderUrl.searchParams.get('v');

        if (loaderVersion) {
            assetUrl.searchParams.set(
                'v',
                loaderVersion
            );
        }

        return assetUrl.href;
    }

    throw new Error(
        'Calculator asset URL is not configured'
    );
}
    function loadScript(config) {
        if(typeof window[config.initFnName]==='function')return Promise.resolve();
        if(scriptLoads.has(config.file))return scriptLoads.get(config.file);
        const job=new Promise((resolve,reject)=>{
            const script=document.createElement('script');script.src=buildAssetUrl(config.file);script.async=true;
            script.onload=()=>{if(typeof window[config.initFnName]==='function')resolve();else{scriptLoads.delete(config.file);script.remove();reject(new Error('Calculator initialization function missing'));}};
            script.onerror=()=>{scriptLoads.delete(config.file);script.remove();reject(new Error('Calculator download failed'));};document.head.appendChild(script);
        });
        scriptLoads.set(config.file,job);job.catch(()=>scriptLoads.delete(config.file));return job;
    }
    function initCalculator(container) {
        if(container.dataset.initialized==='true')return Promise.resolve();
        if(initJobs.has(container))return initJobs.get(container);
        const config=CALCULATORS[container.dataset.calc];if(!config)return Promise.resolve();
        if(!config.available){container.textContent='This calculator is not available yet.';return Promise.resolve();}
        const job=(async()=>{
            container.innerHTML='<p role="status">Loading calculator…</p>';
            try {await loadScript(config);await window[config.initFnName](container);container.dataset.initialized='true';}
            catch(err) {console.error('GWB calculator:',err);container.innerHTML='<p role="status">The calculator could not load. You can still use the application table above.</p><button type="button">Try again</button>';container.querySelector('button').addEventListener('click',()=>initCalculator(container));}
            finally{initJobs.delete(container);}
        })();initJobs.set(container,job);return job;
    }
    function initAllCalculators(){return Promise.all(Array.from(document.querySelectorAll('.gwb-calculator[data-calc]'),initCalculator));}
    Object.assign(GWB,{reinitCalculators:initAllCalculators,calculators:CALCULATORS,loadScript,buildAssetUrl,parseVariants,parseWeight,normalizeVariants,recommendPackage,recommendPackages,formatPrice,escapeHtml,getProductData,addItems,routeRoot,version:'5.3.1'});
    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',initAllCalculators,{once:true});else initAllCalculators();
    document.addEventListener('shopify:section:load',initAllCalculators);
})();